/*
 * Profile → "My newsletters" (design §8 "NEW: My newsletters"; row
 * _design/DESIGN-BRAND-SIGNUP-AND-NEWSLETTERS-V1). Behind
 * VITE_MY_NEWSLETTERS_ENABLED; flag off ⇒ no request and nothing renders.
 *
 * Wire: my-api GET /me/newsletters (BFF over marketing-service's merged
 * subscription state, which marketing owns):
 *   404 (my-api flag off)         → nothing
 *   {available:false} / failure   → "Newsletters aren't available right now"
 *   {available:true, newsletters} → one row per org unit: brand name, on/off
 *                                   switch, "since {date}", source label
 * editable:false rows show a lock instead of the switch:
 *   reason contact_support        → the org-wide stop + Contact support
 *   reason parent_managed / minor → "Managed by your parent or guardian"
 *   not_contactable[]             → a short reason per code
 *
 * The switch is OPTIMISTIC: it flips at once, POSTs (the PUT's alias), and
 * shows a subtle "updating" hint, because marketing applies the change up to
 * 15 minutes later (the BFF answers pending:true meanwhile). An error reverts
 * the switch and says so.
 *
 * Arriving from /join/confirm (router state `joined`), the confirmed brand is
 * highlighted and the optional "Help us tailor events" card shows.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { loadSession } from '../auth/session'
import { newslettersEnabled } from '../lib/brandSignupFlag'
import { fetchMyProfile, fetchNewsletters, myApiBase, MyApiError, setNewsletter, type Newsletter, type NewslettersRead } from '../data/myApi'
import { formatPlainDate } from '../data/legacyHistory'
import { supportMailtoHref } from '../data/support'
import { NEAREST_CITY_EDIT_PATH } from './NearestCityPrompt'
import { useLocale, type MessageKey, type TranslateFn } from '../i18n/useLocale'

export const NEWSLETTERS_PATH = '/profile/newsletters'

/** What /join/confirm hands over (router state). orgUnitId null = an
 *  already-confirmed link (409): no highlight. */
export interface JoinedState {
  orgUnitId: string | null
  displayName: string | null
  optedIn: boolean
}

const headingStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em', fontWeight: 400,
  textTransform: 'uppercase', color: 'var(--text-faint)', margin: '0 2px 8px',
}
const cardStyle: CSSProperties = { background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 15 }
const quietStyle: CSSProperties = { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, padding: '0 2px' }
const metaStyle: CSSProperties = { fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.45 }

const SOURCE_KEYS: Record<string, MessageKey> = {
  signup_page: 'newsletters.source.signup_page',
  profile_page: 'newsletters.source.profile_page',
  newsletters_page: 'newsletters.source.newsletters_page',
  hello_world_cta: 'newsletters.source.hello_world_cta',
  company_basis: 'newsletters.source.company_basis',
  unit_unsubscribe: 'newsletters.source.unit_unsubscribe',
  organization_request: 'newsletters.source.organization_request',
  import: 'newsletters.source.import',
}

const LOCK_KEYS: Record<string, MessageKey> = {
  minor: 'newsletters.lock.minor',
  hard_bounced: 'newsletters.lock.hard_bounced',
  erased: 'newsletters.lock.erased',
  age_not_attested: 'newsletters.lock.age_not_attested',
}

/** The lock line for a non-editable row (null for an editable one). */
export function lockKey(n: Newsletter): MessageKey | 'contact_support' | null {
  if (n.editable) return null
  if (n.reason === 'contact_support') return 'contact_support'
  if (n.reason === 'parent_managed') return 'newsletters.lock.minor'
  for (const r of n.not_contactable) if (LOCK_KEYS[r]) return LOCK_KEYS[r]
  return 'newsletters.lock.other'
}

function sinceText(since: string | null, t: TranslateFn, lang: 'en' | 'ar'): string | null {
  if (!since) return null
  const ymd = since.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null
  return t('newsletters.since', { date: formatPlainDate(ymd, lang) })
}

/** null = render nothing (flag off / no session / my-api flag off). */
export function useNewsletters(fetchImpl?: typeof fetch, env: Record<string, string | undefined> = import.meta.env): NewslettersRead | null | 'loading' {
  const enabled = newslettersEnabled(env)
  const base = myApiBase(env)
  const [view, setView] = useState<NewslettersRead | null | 'loading'>(() => (enabled && base && loadSession() ? 'loading' : null))
  useEffect(() => {
    if (!enabled || !base) return
    const session = loadSession()
    if (!session) return
    let cancelled = false
    fetchNewsletters(session.idToken, base, fetchImpl ?? fetch)
      .then((v) => { if (!cancelled) setView(v) })
      .catch((e) => {
        if (cancelled) return
        setView(e instanceof MyApiError && e.status === 404 ? null : { available: false, newsletters: [] })
      })
    return () => { cancelled = true }
  }, [enabled, base, fetchImpl])
  return view
}

export function MyNewslettersSection({ fetchImpl, env = import.meta.env }: {
  fetchImpl?: typeof fetch
  env?: Record<string, string | undefined>
}) {
  const { t, lang } = useLocale()
  const location = useLocation()
  const view = useNewsletters(fetchImpl, env)
  const joined = (location.state as { joined?: JoinedState } | null)?.joined ?? null
  const highlightId = (joined?.orgUnitId ?? new URLSearchParams(location.search).get('joined') ?? '').toLowerCase() || null
  const sectionRef = useRef<HTMLElement | null>(null)
  const onPath = location.pathname.replace(/\/+$/, '') === NEWSLETTERS_PATH

  useEffect(() => {
    if (onPath && view && view !== 'loading') sectionRef.current?.scrollIntoView?.({ block: 'start' })
  }, [onPath, view])

  if (!newslettersEnabled(env) || view === null) return null

  return (
    <section ref={sectionRef} id="profile.newsletters" data-testid="newsletters-section" aria-labelledby="newsletters-heading" style={{ marginBottom: 22 }}>
      <h2 id="newsletters-heading" style={headingStyle}>{t('newsletters.heading')}</h2>
      {joined && (
        <p data-testid="newsletters-joined" role="status" style={{ ...quietStyle, color: 'var(--text)', marginBottom: 8 }}>
          {joined.optedIn && joined.displayName ? t('newsletters.joined', { brand: joined.displayName }) : t('newsletters.confirmed')}
        </p>
      )}
      {joined && <TailorCard fetchImpl={fetchImpl} env={env} />}
      {view === 'loading' ? (
        <div role="status" style={quietStyle}>{t('join.loading')}</div>
      ) : !view.available ? (
        <div data-testid="newsletters-unavailable" style={quietStyle}>{t('newsletters.unavailable')}</div>
      ) : view.newsletters.length === 0 ? (
        <div data-testid="newsletters-empty" style={quietStyle}>{t('newsletters.empty')}</div>
      ) : (
        <div style={{ ...cardStyle, padding: '4px 0' }}>
          {view.newsletters.map((n, i) => (
            <NewsletterRow
              key={n.org_unit_id}
              initial={n}
              first={i === 0}
              highlighted={highlightId !== null && n.org_unit_id.toLowerCase() === highlightId}
              fallbackName={highlightId !== null && n.org_unit_id.toLowerCase() === highlightId ? joined?.displayName ?? null : null}
              fetchImpl={fetchImpl}
              env={env}
              t={t}
              lang={lang}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function NewsletterRow({ initial, first, highlighted, fallbackName, fetchImpl, env, t, lang }: {
  initial: Newsletter
  first: boolean
  highlighted: boolean
  fallbackName: string | null
  fetchImpl?: typeof fetch
  env: Record<string, string | undefined>
  t: TranslateFn
  lang: 'en' | 'ar'
}) {
  const [on, setOn] = useState(initial.state === 'subscribed')
  const [pending, setPending] = useState(initial.pending === true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)
  const name = initial.brand_name ?? fallbackName ?? t('newsletters.unnamedBrand')
  const lock = lockKey(initial)
  const sourceKey = initial.source ? SOURCE_KEYS[initial.source] : undefined
  const meta = [sinceText(initial.since, t, lang), sourceKey ? t(sourceKey) : null].filter(Boolean).join(' · ')
  const support = lock === 'contact_support' ? supportMailtoHref({}, env) : null

  const toggle = () => {
    if (busy || lock) return
    const session = loadSession()
    const base = myApiBase(env)
    if (!session || !base) {
      setError(true)
      return
    }
    const prev = on
    const next = !on
    // Optimistic: show the new state now; revert on error.
    setOn(next)
    setBusy(true)
    setError(false)
    setNewsletter(session.idToken, base, initial.org_unit_id, next, fetchImpl ?? fetch)
      .then(() => setPending(true))
      .catch(() => {
        setOn(prev)
        setError(true)
      })
      .finally(() => setBusy(false))
  }

  return (
    <div
      data-testid="newsletter-row"
      data-org-unit={initial.org_unit_id}
      data-highlighted={highlighted ? 'true' : undefined}
      style={{
        display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 15px',
        borderTop: first ? undefined : '1px solid var(--hairline-06)',
        ...(highlighted ? { boxShadow: 'inset 3px 0 0 var(--accent)', background: 'var(--tint-teal, transparent)' } : {}),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div data-testid="newsletter-name" style={{ fontSize: 14, fontWeight: 700 }}>{name}</div>
          {meta && <div data-testid="newsletter-meta" style={metaStyle}>{meta}</div>}
        </div>
        {lock ? (
          <span data-testid="newsletter-locked" aria-label={t(on ? 'newsletters.on' : 'newsletters.off')} style={{ fontSize: 12, color: 'var(--text-faint)', flex: 'none' }}>
            {t(on ? 'newsletters.on' : 'newsletters.off')}
          </span>
        ) : (
          <Switch on={on} busy={busy} label={t('newsletters.switchLabel', { brand: name })} onToggle={toggle} />
        )}
      </div>
      {lock === 'contact_support' && (
        <div data-testid="newsletter-lock" style={metaStyle}>
          {t('newsletters.lock.contactSupport')}{' '}
          {support && (
            <a data-testid="newsletter-support" href={support} style={{ fontWeight: 700, color: 'var(--tru-status-teal-bright)' }}>
              {t('newsletters.lock.contactSupportLink')}
            </a>
          )}
        </div>
      )}
      {lock && lock !== 'contact_support' && (
        <div data-testid="newsletter-lock" style={metaStyle}>{t(lock)}</div>
      )}
      {!lock && pending && !error && (
        <div data-testid="newsletter-updating" role="status" style={{ ...metaStyle, color: 'var(--text-faint)' }}>{t('newsletters.updating')}</div>
      )}
      {error && (
        <div data-testid="newsletter-error" role="alert" style={{ ...metaStyle, color: 'var(--warn)' }}>{t('newsletters.error')}</div>
      )}
    </div>
  )
}

function Switch({ on, busy, label, onToggle }: { on: boolean; busy: boolean; label: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-busy={busy || undefined}
      data-testid="newsletter-switch"
      onClick={onToggle}
      style={{
        flex: 'none', position: 'relative', width: 46, height: 28, borderRadius: 14, padding: 0, cursor: 'pointer',
        border: '1px solid var(--hairline-14)', background: on ? 'var(--accent)' : 'var(--surface-2)',
        transition: 'background .15s', opacity: busy ? 0.75 : 1,
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute', top: 3, insetInlineStart: on ? 21 : 3, width: 20, height: 20, borderRadius: '50%',
          background: on ? 'var(--accent-on, #fff)' : 'var(--text-faint)', transition: 'inset-inline-start .15s',
        }}
      />
    </button>
  )
}

/**
 * "Help us tailor events" — optional and skippable, shown once after a
 * confirmed signup. PR-B asks only for HOME CITY, and only when the profile
 * has none. It reuses the existing city component and write: the closed
 * nearest-city picker in wizard step 3 (residence + city, PUT /me/profile),
 * opened as an account-hub edit — the same entry the nearest-city prompt's
 * Change uses.
 *
 * TODO(PR-C, brand signup): the gym/club question. It needs a my-api BFF
 * over settings' org-field `if_absent` PUT that isn't built yet — do not
 * build it here.
 */
export function TailorCard({ fetchImpl, env = import.meta.env }: { fetchImpl?: typeof fetch; env?: Record<string, string | undefined> }) {
  const { t } = useLocale()
  const navigate = useNavigate()
  const [show, setShow] = useState(false)
  const base = myApiBase(env)

  useEffect(() => {
    const session = loadSession()
    if (!session || !base) return
    let cancelled = false
    fetchMyProfile(session.idToken, base, fetchImpl ?? fetch)
      .then((r) => { if (!cancelled && !r.profile?.city?.trim()) setShow(true) })
      .catch(() => { /* never prompt on a guess */ })
    return () => { cancelled = true }
  }, [fetchImpl, base])

  if (!show) return null
  return (
    <div data-testid="tailor-card" style={{ ...cardStyle, padding: '14px 16px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ ...headingStyle, margin: 0 }}>{t('tailor.kicker')}</p>
      <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{t('tailor.title')}</p>
      <p style={{ margin: 0, ...metaStyle }}>{t('tailor.body')}</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" data-testid="tailor-add-city" onClick={() => navigate(NEAREST_CITY_EDIT_PATH)} style={{ minHeight: 40, borderRadius: 9, padding: '0 16px', border: 0, background: 'var(--accent)', color: 'var(--accent-ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
          {t('tailor.addCity')}
        </button>
        <button type="button" data-testid="tailor-skip" onClick={() => setShow(false)} style={{ minHeight: 40, borderRadius: 9, padding: '0 16px', border: '1px solid var(--hairline-12)', background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          {t('tailor.skip')}
        </button>
      </div>
    </div>
  )
}
